import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { Link } from "react-router-dom";
import { Droplets, Wind, FlaskConical, ArrowRight } from "lucide-react";

const PoolSystem = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="The Pool System" 
        description="Understanding how circulation, cleaning, and chemistry work together"
      >
        <section className="space-y-8">
          {/* The Three C's */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-4">The Three C's of Pool Care</h2>
            <p className="text-muted-foreground mb-4">
              Every pool operates as an integrated system. Master these three fundamentals and you'll understand 
              how to diagnose any pool problem.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-background rounded-lg p-4 border">
                <div className="flex items-center gap-2 mb-2">
                  <Wind className="h-5 w-5 text-primary" />
                  <span className="font-semibold">Circulation</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Moving water through the system distributes chemicals and filters debris
                </p>
              </div>
              <div className="bg-background rounded-lg p-4 border">
                <div className="flex items-center gap-2 mb-2">
                  <Droplets className="h-5 w-5 text-primary" />
                  <span className="font-semibold">Cleaning</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Physical removal of debris and prevention of buildup on surfaces
                </p>
              </div>
              <div className="bg-background rounded-lg p-4 border">
                <div className="flex items-center gap-2 mb-2">
                  <FlaskConical className="h-5 w-5 text-primary" />
                  <span className="font-semibold">Chemistry</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Balanced water prevents algae, protects equipment, and ensures swimmer safety
                </p>
              </div>
            </div>
          </div>

          {/* Why They're Connected */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Why These Aren't Separate Tasks</h2>
            <p className="text-muted-foreground mb-4">
              The Three C's are interdependent. When one fails, the others can't compensate:
            </p>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span><strong className="text-foreground">Poor circulation</strong> = chemicals don't distribute evenly, 
                dead spots develop algae, debris settles instead of reaching the skimmer</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span><strong className="text-foreground">Skipped cleaning</strong> = organic debris consumes chlorine faster, 
                biofilm builds on surfaces, staining becomes permanent</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span><strong className="text-foreground">Unbalanced chemistry</strong> = even perfect circulation and cleaning 
                can't prevent algae, scale, or corrosion</span>
              </li>
            </ul>
          </div>

          {/* The Flow Loop */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">How Water Flows Through the System</h2>
            <div className="bg-card border rounded-lg p-6">
              <div className="flex flex-wrap items-center justify-center gap-2 text-sm">
                <span className="bg-primary/20 px-3 py-1 rounded-full font-medium">Pool/Spa</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <span className="bg-muted px-3 py-1 rounded-full">Skimmer</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <span className="bg-muted px-3 py-1 rounded-full">Pump</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <span className="bg-muted px-3 py-1 rounded-full">Filter</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <span className="bg-muted px-3 py-1 rounded-full">Heater</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <span className="bg-muted px-3 py-1 rounded-full">Salt Cell</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <span className="bg-muted px-3 py-1 rounded-full">Return Jets</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <span className="bg-primary/20 px-3 py-1 rounded-full font-medium">Pool/Spa</span>
              </div>
              <p className="text-sm text-muted-foreground text-center mt-4">
                Understanding this loop helps you trace any circulation issue from symptom to source.
              </p>
            </div>
          </div>

          {/* San Diego Context */}
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-3">San Diego & Rancho Santa Fe Context</h3>
            <ul className="space-y-2 text-muted-foreground">
              <li>• <strong className="text-foreground">Year-round sun</strong> = higher UV exposure = faster chlorine consumption</li>
              <li>• <strong className="text-foreground">No true winter</strong> = algae risk never goes away, but no winterization needed</li>
              <li>• <strong className="text-foreground">Hard water</strong> = calcium scale is common, watch for buildup on tiles and cells</li>
              <li>• <strong className="text-foreground">Santa Ana winds</strong> = heavy debris days require extra skimming time</li>
              <li>• <strong className="text-foreground">Drought considerations</strong> = water conservation matters, minimize backwash waste</li>
            </ul>
          </div>

          {/* Premium Client Context */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Premium Client Expectations</h2>
            <p className="text-muted-foreground mb-4">
              BlueLux serves premium residential clients who expect:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li><strong className="text-foreground">Sparkling water</strong> — every visit, no exceptions</li>
              <li><strong className="text-foreground">Quiet equipment</strong> — variable speed pumps at optimal settings</li>
              <li><strong className="text-foreground">Proactive service</strong> — catch problems before clients notice them</li>
              <li><strong className="text-foreground">Minimal disruption</strong> — efficient visits, professional appearance</li>
              <li><strong className="text-foreground">Clear communication</strong> — if there's an issue, they want to know</li>
            </ul>
          </div>

          {/* Why It Matters */}
          <div className="bg-card border-2 border-primary rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-3">Why This Matters</h3>
            <p className="text-muted-foreground">
              Understanding the pool as a system — not just a checklist — helps you diagnose problems faster, 
              explain issues to customers clearly, and deliver consistently excellent results. When something 
              goes wrong, trace it back through the Three C's to find the root cause.
            </p>
          </div>

          {/* Related Topics */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Deep Dive Into Each Area</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <Link to="/wiki/fundamentals/circulation" className="block p-4 bg-card border rounded-lg hover:border-primary transition-colors">
                <Wind className="h-5 w-5 text-primary mb-2" />
                <span className="font-medium">Circulation Basics</span>
                <p className="text-sm text-muted-foreground mt-1">Run times, pressure, jet positioning</p>
              </Link>
              <Link to="/wiki/fundamentals/cleaning" className="block p-4 bg-card border rounded-lg hover:border-primary transition-colors">
                <Droplets className="h-5 w-5 text-primary mb-2" />
                <span className="font-medium">Cleaning Essentials</span>
                <p className="text-sm text-muted-foreground mt-1">Brushing, skimming, vacuuming</p>
              </Link>
              <Link to="/wiki/fundamentals/chemistry" className="block p-4 bg-card border rounded-lg hover:border-primary transition-colors">
                <FlaskConical className="h-5 w-5 text-primary mb-2" />
                <span className="font-medium">Water Chemistry 101</span>
                <p className="text-sm text-muted-foreground mt-1">Testing, balancing, troubleshooting</p>
              </Link>
            </div>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default PoolSystem;